import React, { useState, useEffect, useRef, Fragment } from "react";
import { ListingTable } from "components/listing";
import { AccountService, SelfReportingService, UserService } from "services";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Breadcrumbs } from "components/navigation";
import { NotificationToaster } from "components/notifications";
import { ShowHide } from "components/layout";
import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { useDebounce } from 'hooks/useDebounce';
import { FormTextInput } from "components/form-fields";
import { Tooltip } from "components/tooltip/Tooltip";
import PropTypes from "prop-types";

export function ErroredSchedules(props) {
  const defaultRecordSize = 25;
  const debounceTimeout = 750;

  const [totalRecords, setTotalRecords] = useState(0);
  const [tableRows, setTableRows] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [erroredSchedules, setErroredSchedules] = useState(null);
  const noDataMessage = "The schedule does not have any errors";
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRecordSize);
  const requiredAction = "Internal";
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
  const inputSearchRef = useRef(null);

  const tableHeaders = ["Member Number", "Schedule Name", "Error Messages", "Actions"];


  // Fetch accounts once on mount
  useEffect(() => {
    AccountService.getAllAccounts()
      .then((response) => {
        setAccounts(response.data || []);
      }, () => {
        NotificationToaster.show(Intent.DANGER, "Could not retrieve accounts, please try again.");
      })
      .catch((error) => {
        NotificationToaster.show(Intent.DANGER, "Could not retrieve accounts, please try again.");
      });
  }, []);

  // Fetch errored schedules when paging/search changes
  useEffect(() => {
    setLoadingData(true);
    SelfReportingService.getErroredSchedules(pageSize, pageNumber, debouncedSearchTerm)
      .then((response) => {
        setErroredSchedules(response);
        setLoadingData(false);
      })
      .catch(() => {
        setLoadingData(false);
        NotificationToaster.show(
          Intent.DANGER,
          "Could not retrieve errored schedules, please try again."
        );
      });
  }, [pageSize, pageNumber, debouncedSearchTerm]);

  // Map errored schedules and accounts when both are loaded
  useEffect(() => {
    if (erroredSchedules && Array.isArray(erroredSchedules.data) && accounts.length > 0) {
      setTotalRecords(erroredSchedules.totalCount);
      const data = erroredSchedules.data.map((d) => {
        let matchedAccount = accounts.find(account => account.id === d.accountId) || {};
        let isRestricted = matchedAccount.restricted === true;

        return [d.memberNumber,
        d.scheduleName,
        <span key={`errors-${d.scheduleId}`}>{
          d.errorReasons.map((e, i) => {
            return (<span key={i}>&bull;&nbsp; {e} <br /></span>);
          })
        }</span>,
        <Tooltip key={`account-${d.accountId}-switch-account-button`} disabled={!isRestricted} content={"You do not have permission to switch to this account"}>
          <Button disabled={isRestricted} key={`switch-schedule-${d.scheduleId}`} onClick={() => switchToSchedule(d.accountId, d.scheduleId, d.lastInstanceId)} icon="swap-horizontal" iconOnly={true} text="Switch to schedule" large={false} minimal={true}></Button>
        </Tooltip>
        ];
      });
      setTableRows(data);
      setTimeout(() => {
        if (inputSearchRef.current != null) {
          inputSearchRef.current.focus();
        }
      });
    }
  }, [erroredSchedules, accounts]);

  function onPagingChange(newPageNumber, newPageSize) {
    setPageNumber(newPageNumber);
    setPageSize(newPageSize);
  }

  function switchToSchedule(accountId, scheduleId, instanceId) {
    UserService.switchAccount(accountId).then(function () {
      if (instanceId == null) {
        window.location.href = `/schedule/${scheduleId}`;
      } else {
        window.location.href = `/schedule/${scheduleId}/runs/${instanceId}`;
      }
    }, function () {
      NotificationToaster.show(
        Intent.DANGER,
        "Could not switch account, please try again."
      );
    });
  }

  function onSearchChange(item) {
    setSearchTerm(item.target.value);
  }

  return (
    <UserAccess
      perform={requiredAction}
      yes={() => (
        <div className="row">
          <Breadcrumbs items={props.breadcrumbs} />

          <h1>Errored Schedules</h1>
          <div className="intro-text">
            <p>From here you can see all the schedules that have failed</p>
          </div>

          <ShowHide
            evaluator={tableRows.length === 0 && !loadingData && debouncedSearchTerm === ""}
            hide={(
              <Fragment>
                <div className="pull-left">
                  <div className="inline-items">
                    <FormTextInput inputRef={inputSearchRef} placeholder="Member Number Search" onChange={onSearchChange} value={searchTerm} large disabled={loadingData} icon="search" id="scheduler-search-field" />
                  </div>
                </div>
              </Fragment>
            )}
          />

          <ListingTable
            id="listing-table-errored-schedules"
            headers={tableHeaders}
            onPagingChange={onPagingChange}
            loadingData={loadingData}
            noDataMessage={noDataMessage}
            data={tableRows}
            totalRecordCount={totalRecords}
            pageable={true}
          />

        </div>
      )}
      no={() => <PageRestricted />}
    />
  );
}

ErroredSchedules.propTypes = {
  breadcrumbs: PropTypes.array,
};
